import type { RendererElement } from 'vue'
import { underscoreToHyphen } from '@/utils/string.js'

const FORM_SELECTORS = [
  '.rich-text-editor[name]',
  '.base-combobox[name]',
  'nord-input[name]',
  'nord-select[name]',
  'nord-date-picker[name]',
  'nord-textarea[name]',
  'nord-range[name]',
  'nord-checkbox[name]',
  'nord-fieldset[name]',
  'nord-toggle[name]',
] as const

const FIELDSET_FORM_ELEMENTS = ['NORD-CHECKBOX', 'NORD-TOGGLE'] as const

type FieldsetFormElement = (typeof FIELDSET_FORM_ELEMENTS)[number]

/**
 * Sets the data-pc attribute on an element
 */
const setDataPcAttribute = (element: Element, prefix: string, id: string) => {
  element.setAttribute('data-pc', `${prefix}#${id}`)
}

/**
 * Sanitizes template string by removing undefined values and leading hashes
 */
const sanitizeTemplateString = (value: string) => {
  return value?.replace('undefined#', '')?.replace(/^#+/, '')
}

/**
 * Gets the current route prefix
 */
const getRoutePrefix = () => {
  const { name } = useRoute()
  return name?.replace('provetId-', '')
}

/**
 * Generates modifier for checkbox/toggle elements within fieldsets
 */
const getElementModifier = (formElement: Element) => {
  const isFieldsetChild = formElement.closest('nord-fieldset')
  const isCheckboxOrToggle = FIELDSET_FORM_ELEMENTS.includes(
    formElement.tagName as FieldsetFormElement,
  )

  if (isFieldsetChild && isCheckboxOrToggle) {
    const dataValue = formElement.getAttribute('data-value')
    return dataValue ? `-${dataValue}` : ''
  }

  return ''
}

/**
 * Processes a single form element and sets its data-pc attribute
 */
const processFormElement = (
  formElement: Element,
  baseValue: string,
  prefix: string,
  useDashed: boolean = false,
) => {
  const nameAttribute = formElement.getAttribute('name')
  if (!nameAttribute) {
    return
  }

  const nameAttr = underscoreToHyphen(nameAttribute)
  const modifier = getElementModifier(formElement)
  const separator = useDashed ? '-' : '#'
  const normalizedId = `${baseValue}${separator}${nameAttr}${modifier}`

  setDataPcAttribute(formElement, prefix, normalizedId)
}

/**
 * Processes all form elements within a form container
 */
const processFormElements = async (
  formElement: Element,
  baseValue: string,
  prefix: string,
  useDashed: boolean = false,
) => {
  await nextTick()

  const formElements = formElement.querySelectorAll(FORM_SELECTORS.join(', '))

  for (const element of formElements) {
    processFormElement(element, baseValue, prefix, useDashed)
  }
}

/**
 * Main handler for setting data-pc attributes
 */
const handleDataPcDirective = async (
  element: RendererElement,
  { value, modifiers }: DirectiveBinding<string>,
  vNode: VNode,
) => {
  if (!value) {
    return
  }

  const prefix = getRoutePrefix()
  const normalizedId = sanitizeTemplateString(value)
  const useDashed = modifiers.dashed === true

  const domElement = element as Element

  // Set data-pc on the main element (always uses #)
  setDataPcAttribute(domElement, prefix, normalizedId)

  // Process form elements if this is a form (uses - if dashed modifier is applied)
  if (vNode.type === 'form') {
    await processFormElements(domElement, value, prefix, useDashed)
  }
}

/**
 * Creates a complete data-pc value with route prefix
 * This function can be used in render functions (h()) where the directive doesn't work properly
 */
export const createDataPcValue = (value: string) => {
  const prefix = getRoutePrefix()
  const normalizedId = sanitizeTemplateString(value)
  return `${prefix}#${normalizedId}`
}

type ElementWithCleanup = RendererElement & {
  _stopDataPcWatch?: VoidFunction
}

export default defineNuxtPlugin(({ vueApp }) => {
  const route = useRoute()

  vueApp.directive('data-pc', {
    mounted(el, binding: DirectiveBinding<string>, vNode: VNode) {
      // Initial setup
      void handleDataPcDirective(el, binding, vNode)

      // Watch for route changes and update attributes
      const stopWatch = watch(
        () => route.name,
        () => void handleDataPcDirective(el, binding, vNode),
      )

      // Store the cleanup function on the element
      const elementWithCleanup = el as ElementWithCleanup
      elementWithCleanup._stopDataPcWatch = stopWatch
    },
    updated(el, binding: DirectiveBinding<string>, vNode: VNode) {
      void handleDataPcDirective(el, binding, vNode)
    },
    unmounted(el) {
      const elementWithCleanup = el as ElementWithCleanup

      // Clean up the watcher when the element is unmounted
      const stopWatch = elementWithCleanup._stopDataPcWatch
      if (stopWatch) {
        stopWatch()
        elementWithCleanup._stopDataPcWatch = undefined
      }
    },
  })
})
